import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Grid } from "@mui/material";
import { type FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { trackEvent } from "@/utils/analytics";
import type { WeekData } from "@/utils/models";
import { useShiftInsightsWeek } from "../chartContainer/useShiftInsightsWeek";
import Style from "./DateSlider.module.scss";
import { formatDateRange } from "./formatDateRange";
import { useArrowKeys } from "./useArrowKeys";
import { WeekDateControl } from "./WeekDateControl";

type DateSliderProps = {
	isFullDates?: boolean;
	/** With `isFullDates`, the weeks whose span it shows; the old list's when not given. */
	fullDatesWeeks?: WeekData[];
	isNoDates?: boolean;
	hideArrows?: boolean;
	isWeekControl?: boolean;
};

/** The week slider: the Week view's date control (`isWeekControl`), or the plain slider other pages show. */
export const DateSlider: FC<DateSliderProps> = ({ isWeekControl, ...sliderProps }) =>
	isWeekControl ? <WeekDateControl /> : <PageDateSlider {...sliderProps} />;

/** Planning's label: the span of the weeks it shows, with no arrows. */
const SpanDateLabel: FC<{ weeks: WeekData[] }> = ({ weeks }) => {
	const { i18n } = useTranslation();
	// Only hospital demos (Hospital type + inhouse2).
	const { isHospitalView: isHospitalDemoView } = useHospitalData();
	const formattedDateRange = useMemo(
		() =>
			formatDateRange(
				weeks.at(0)?.weeklyShifts.at(0)?.days.at(0)?.date,
				weeks.at(-1)?.weeklyShifts.at(0)?.days.at(-1)?.date,
				i18n.language,
				isHospitalDemoView,
			),
		[weeks, i18n.language, isHospitalDemoView],
	);
	return (
		<div className={Style.parentContainer}>
			<div className={Style.container}>
				<Grid container sx={{ justifyContent: isHospitalDemoView ? "center" : "space-between", alignItems: "center", flexWrap: "nowrap" }}>
					<Grid>
						<div className={Style.label} style={{ color: "primary.main" }}>
							<div className={Style.textWrapper}>{formattedDateRange}</div>
						</div>
					</Grid>
				</Grid>
			</div>
		</div>
	);
};

const PageDateSlider: FC<Omit<DateSliderProps, "isWeekControl">> = ({ isFullDates, fullDatesWeeks, isNoDates, hideArrows }) => {
	const { hospitalData } = useHospitalData();
	if (isFullDates && !isNoDates) {
		// DEFAULT: the old list's weeks, for a caller that doesn't pass its own.
		return <SpanDateLabel weeks={fullDatesWeeks ?? hospitalData.weeks} />;
	}
	return <WeekPageSlider isNoDates={isNoDates} hideArrows={hideArrows} />;
};

// Shift Insights: steps through its weeks (`useShiftInsightsWeek`), without touching the address bar.
const WeekPageSlider: FC<Pick<DateSliderProps, "isNoDates" | "hideArrows">> = ({ isNoDates, hideArrows }) => {
	const { i18n } = useTranslation();
	// Only hospital demos (Hospital type + inhouse2).
	const { isHospitalView: isHospitalDemoView } = useHospitalData();
	const insights = useShiftInsightsWeek();
	const { weekIndex, hasPreviousWeek, hasNextWeek, showPreviousWeek, showNextWeek } = insights;
	const shownWeekStart = insights.week?.weeklyShifts[0]?.days[0]?.date;
	const isRTL = i18n.dir() === "rtl";

	const trackStep = useCallback(
		(event: string, weeksMoved: number) =>
			trackEvent(event, {
				previous_week_index: weekIndex,
				new_week_index: weekIndex + weeksMoved,
				week_start_date: shownWeekStart?.plus({ weeks: weeksMoved }).toFormat("yyyy-MM-dd"),
				week_end_date: shownWeekStart?.plus({ weeks: weeksMoved, days: 6 }).toFormat("yyyy-MM-dd"),
			}),
		[weekIndex, shownWeekStart],
	);

	const handlePrevClick = useCallback(() => {
		if (!hasPreviousWeek) return;
		showPreviousWeek();
		trackStep("previous_week_clicked", -1);
	}, [hasPreviousWeek, showPreviousWeek, trackStep]);

	const handleNextClick = useCallback(() => {
		if (!hasNextWeek) return;
		showNextWeek();
		trackStep("next_week_clicked", 1);
	}, [hasNextWeek, showNextWeek, trackStep]);

	useArrowKeys(handlePrevClick, handleNextClick);

	const formattedDateRange = useMemo(
		() => formatDateRange(shownWeekStart, insights.week?.weeklyShifts.at(0)?.days.at(-1)?.date, i18n.language, isHospitalDemoView),
		[shownWeekStart, insights.week, i18n.language, isHospitalDemoView],
	);
	const hasArrows = (hasPreviousWeek || hasNextWeek) && !hideArrows;
	const previousSx = { color: "primary.main", fontSize: "20px", cursor: hasPreviousWeek ? "pointer" : "default" };
	const nextSx = { color: "primary.main", fontSize: "20px", cursor: hasNextWeek ? "pointer" : "default" };
	const previousOpacity = { opacity: hasPreviousWeek ? 1 : 0.5 };
	const nextOpacity = { opacity: hasNextWeek ? 1 : 0.5 };

	return (
		<div className={Style.parentContainer}>
			{!isNoDates && (
				<div className={Style.container}>
					<Grid
						container
						sx={{
							// Hospital demo centers the arrows around the label (< Aug 2026 >);
							// everyone else keeps the original split (arrows left, label right).
							justifyContent: isHospitalDemoView ? "center" : "space-between",
							alignItems: "center",
							flexWrap: "nowrap",
							gap: isHospitalDemoView ? "8px" : 0,
						}}
					>
						<Grid>
							<div style={{ display: "flex", alignItems: "center", gap: "0px" }}>
								{hasArrows && (
									<>
										{isRTL ? (
											// In RTL mode, next becomes previous and vice versa
											<ArrowForwardIosIcon onClick={handlePrevClick} width="17px" sx={previousSx} style={previousOpacity} />
										) : (
											<ArrowBackIosIcon onClick={handlePrevClick} width="17px" sx={previousSx} style={previousOpacity} />
										)}

										{/* Hospital demo puts the next arrow AFTER the label (< Aug 2026 >),
										    so render it here only for non-demo; the demo one lives past the label. */}
										{!isHospitalDemoView &&
											(isRTL ? (
												<ArrowBackIosIcon width="17px" onClick={handleNextClick} sx={nextSx} style={nextOpacity} />
											) : (
												<ArrowForwardIosIcon width="17px" onClick={handleNextClick} sx={nextSx} style={nextOpacity} />
											))}
									</>
								)}
							</div>
						</Grid>
						<Grid>
							<div className={Style.label} style={{ color: "primary.main" }}>
								<div className={Style.textWrapper}>{formattedDateRange}</div>
							</div>
						</Grid>
						{/* Demo-only: the next arrow sits after the label to form < Aug 2026 >. */}
						{isHospitalDemoView && hasArrows && (
							<Grid>
								<div style={{ display: "flex", alignItems: "center" }}>
									{isRTL ? (
										<ArrowBackIosIcon width="17px" onClick={handleNextClick} sx={nextSx} style={nextOpacity} />
									) : (
										<ArrowForwardIosIcon width="17px" onClick={handleNextClick} sx={nextSx} style={nextOpacity} />
									)}
								</div>
							</Grid>
						)}
					</Grid>
				</div>
			)}
		</div>
	);
};

export default DateSlider;
